import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, ClipboardList, CheckCircle2, XCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { NuevaRequisicionModal } from './NuevaRequisicionModal';

type Req = {
  id: string; folio: string; fecha: string; estado: string;
  tipo_adjudicacion: string; unidad_nombre?: string;
  solicitante_nombre?: string; autorizante_nombre?: string;
  subtotal_estimado: number; iva_estimado: number; total_estimado: number;
  justificacion: string; partida_cog_codigo?: string;
  fecha_autorizacion?: string;
};

export function RequisicionesPage() {
  const [estado, setEstado] = useState<string>('');
  const [open, setOpen] = useState(false);
  const { data, refetch } = useQuery({
    queryKey: ['requisiciones', estado],
    queryFn: () => {
      const p = new URLSearchParams();
      if (estado) p.set('estado', estado);
      return api<Req[]>(`/requisiciones?${p}`);
    },
  });
  const items = data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Adquisiciones · Requisiciones de compra</p>
          <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">Requisiciones</h1>
          <p className="mt-1 text-[14px] text-ink-mute">
            Solicitudes internas de compra. Al autorizar se compromete el presupuesto y se emite póliza.
          </p>
        </div>
        <Button onClick={() => setOpen(true)}><Plus size={16} /> Nueva requisición</Button>
      </div>

      <div className="flex items-center gap-1 rounded-md border border-border bg-surface p-0.5 text-[12px] font-medium w-fit">
        {['', 'borrador', 'solicitada', 'autorizada', 'en_compra', 'cerrada', 'rechazada'].map((e) => (
          <button key={e || 'todas'} onClick={() => setEstado(e)}
            className={cn('rounded px-3 py-1.5 capitalize transition-colors',
              estado === e ? 'bg-brand-500 text-white' : 'text-ink-mute hover:bg-muted')}>
            {e ? e.replace('_', ' ') : 'todas'}
          </button>
        ))}
      </div>

      <Card>
        <CardBody className="p-0">
          {items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <ClipboardList className="h-7 w-7 text-ink-soft" />
              <p className="text-[13px] text-ink-mute">Sin requisiciones.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-[13px]">
                <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                  <tr>
                    <th className="px-5 py-3">Folio</th>
                    <th className="px-5 py-3">Fecha</th>
                    <th className="px-5 py-3">Solicitante / Unidad</th>
                    <th className="px-5 py-3">Justificación</th>
                    <th className="px-5 py-3">COG</th>
                    <th className="px-5 py-3 text-right">Total estimado</th>
                    <th className="px-5 py-3">Estado</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((r) => (
                    <tr key={r.id} className="transition-colors hover:bg-muted/50">
                      <td className="num px-5 py-3 font-semibold">{r.folio}</td>
                      <td className="num px-5 py-3 text-ink-mute">{fmt.date(r.fecha)}</td>
                      <td className="px-5 py-3">
                        <div className="text-ink">{r.solicitante_nombre ?? '—'}</div>
                        <div className="text-[11px] text-ink-mute">{r.unidad_nombre ?? ''}</div>
                      </td>
                      <td className="px-5 py-3 max-w-[260px] truncate">{r.justificacion}</td>
                      <td className="num px-5 py-3">{r.partida_cog_codigo ?? '—'}</td>
                      <td className="num px-5 py-3 text-right font-semibold">{fmt.money(r.total_estimado)}</td>
                      <td className="px-5 py-3"><EstadoPill e={r.estado} /></td>
                      <td className="px-5 py-3 text-right">
                        <Link to={`/app/requisiciones/${r.id}`}><Button variant="ghost" size="sm">Ver</Button></Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <NuevaRequisicionModal open={open} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); refetch(); }} />
    </div>
  );
}

function EstadoPill({ e }: { e: string }) {
  const cfg: Record<string, string> = {
    borrador: 'bg-stone-100 text-ink-mute',
    solicitada: 'bg-sky-50 text-sky-700',
    autorizada: 'bg-emerald-50 text-emerald-700',
    en_compra: 'bg-brand-50 text-brand-700',
    cerrada: 'bg-emerald-50 text-emerald-700',
    rechazada: 'bg-rose-50 text-rose-700',
    cancelada: 'bg-stone-100 text-ink-soft',
  };
  return <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', cfg[e] ?? 'bg-muted text-ink-mute')}>{e.replace('_', ' ')}</span>;
}

// @ts-expect-error
const _ = [CheckCircle2, XCircle];
